import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useNavigationType, useSearchParams } from "react-router-dom";
import useEstadoSessao from "@/hooks/useEstadoSessao";
import { http, registrarDonos, comoConfig } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useEscopo } from "@/components/EscopoPauta";
import { comDe, useEscritorioInfo } from "@/lib/escritorioInfo";
import { marcarParticular } from "@/components/Particular";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Plus, FolderKanban, RefreshCw, Download, X, Search, SlidersHorizontal, Info, MoreHorizontal, Star,
  List, Users, ChevronDown, ChevronRight, ChevronLeft, Check, Lock, LockOpen, ListChecks, ArrowUpDown, Upload,
} from "lucide-react";
import { toTitleCasePt } from "@/lib/format";
import { CASE_AREAS } from "@/lib/caseAreas";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import ProcessoFormDialog from "@/components/ProcessoFormDialog";
import ImportarProcessos from "@/components/ImportarProcessos";
import SugestaoInput from "@/components/SugestaoInput";
import { invalidarProcessosMapa } from "@/hooks/useProcessosMapa";
import useSyncJob from "@/hooks/useSyncJob";
import ProcessoLinha from "@/components/processos/ProcessoLinha";
import FiltrosPainel from "@/components/processos/FiltrosPainel";
import {
  ABAS, ARQ_ROTULO, FILTROS_PADRAO, ORDENS, ORDEM_PADRAO, PARADOS_DEFAULT, PARADOS_KEY, PARADOS_OPTIONS, POR_PAGINA, SIT_OPCOES,
  contarFiltros, filtrarProcessos, itemLabel, localOf, ordenarProcessos, paradosLabel, syncMoment, tribunalLabel,
} from "@/lib/processosUtil";

// Lote 73/99 (histórico): a aba Processos nasceu como lista de cartões com chips; a base de dados (GET /processos)
// devolve por processo status_kind, pendencias, next_hearing, next_prazo, next_action, prioritario, arquivado,
// last_movement_*, datajud_last_*.
// Lote 165 (Robson, 02/11/2026) — remodelagem da tela:
//   • cabeçalho compacto: "Processos" + contagem real, "Atualizado …" (só a última consulta bem-sucedida: a falha
//     não aparece aqui — decisão do Lote 146), "+ Novo processo" e o menu ⋯ (Selecionar, Atualizar monitoramento,
//     Exportar, Como funciona?);
//   • barra: busca, escopo (Meus processos / Todo o escritório), Filtros (painel com "Aplicar filtros") e Visualização;
//   • 3 abas (Todos · Atenção · Prioritários); o resto dos recortes passou para o painel de Filtros;
//   • linhas compactas que expandem (ProcessoLinha), paginação de 25 e seleção em lote "desta página" ou "de todos os
//     resultados". As regras de "próxima ação" mais ricas, ordenação e preferências vêm no Lote 166.
const VIEW_KEY = "iapauta_processos_visao";     // (Lote 165) só a visualização — lido como reserva
const PREFS_KEY = "iapauta_processos_prefs";    // Lote 166: visualização + ordenação + filtros, por pessoa e por escritório
const ABAS_VALIDAS = ["todos", "atencao", "prioritarios"];
const SCROLL_KEY = "iapauta_tela_processos_scroll";

export { tribunalLabel };

// Preferências guardadas neste aparelho para (pessoa, escritório). Sempre lidas com cautela: o que não existir mais
// (cliente apagado, responsável que saiu, tribunal sem processo) é descartado depois que a lista chega.
function lerPrefs(chave) {
  const out = { view: "lista", ordem: ORDEM_PADRAO, filtros: {} };
  try {
    const v = JSON.parse(localStorage.getItem(`${PREFS_KEY}:${chave}`) || "null");
    if (v && typeof v === "object") {
      if (v.view === "cliente") out.view = "cliente";
      if (ORDENS.some((o) => o.key === v.ordem)) out.ordem = v.ordem;
      if (v.filtros && typeof v.filtros === "object") out.filtros = v.filtros;
    } else if (localStorage.getItem(`${VIEW_KEY}:${chave}`) === "cliente") out.view = "cliente";
  } catch (e) { /* sem armazenamento: padrão */ }
  return out;
}
function filtrosDePrefs(raw) {
  const f = { ...FILTROS_PADRAO };
  ["cliente", "area", "tribunal", "local"].forEach((k) => { if (typeof raw[k] === "string" && raw[k]) f[k] = raw[k]; });
  if (Array.isArray(raw.sit)) f.sit = raw.sit.filter((k) => SIT_OPCOES.some((o) => o.key === k));
  return f;      // "cadastro no app" (arquivados/extintos) nunca fica lembrado: toda visita começa nos ativos
}

export default function Processos() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [erroCarga, setErroCarga] = useState(null);   // null | "geral" | "permissao"
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [howOpen, setHowOpen] = useState(false);
  const [importarOpen, setImportarOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [djStatus, setDjStatus] = useState(null);     // { last_at, errors, checked }

  const [searchParams, setSearchParams] = useSearchParams();
  // Lote 117: "?novo=NUMERO" abre "Cadastrar processo" já com o número.
  const [novoInicial, setNovoInicial] = useState(null);
  useEffect(() => {
    const n = searchParams.get("novo");
    if (n) {
      setNovoInicial({ numero: n });
      setOpen(true);
      const sp = new URLSearchParams(searchParams); sp.delete("novo"); setSearchParams(sp, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Lote 131: o que a pessoa escolheu fica guardado na sessão — "Voltar" de um processo volta para a mesma lista.
  const [tab, setTabRaw] = useEstadoSessao("processos_aba_v2", "todos", {
    validos: ABAS_VALIDAS,
    forcado: searchParams.get("filtro") === "prioritarios" ? "prioritarios" : undefined,
  });
  const clienteUrl = searchParams.get("cliente");   // Lote 91: a página Clientes linka pra cá com ?cliente=ID
  const navType = useNavigationType();
  const { user } = useAuth() || {};
  const meuId = user?.user_id;
  const noEscritorio = !!user?.escritorio;
  const chavePrefs = `${meuId || "-"}:${user?.escritorio?.org_id || "-"}`;
  const prefsRef = useRef(null);
  if (prefsRef.current === null) prefsRef.current = lerPrefs(chavePrefs);
  const [f, setF] = useEstadoSessao("processos_filtros_v2", () => filtrosDePrefs(prefsRef.current.filtros), {
    forcado: clienteUrl ? { ...FILTROS_PADRAO, cliente: clienteUrl }
      : searchParams.get("aba") === "arquivados" ? { ...FILTROS_PADRAO, arq: "pausados" } : undefined,
  });
  const [search, setSearchRaw] = useEstadoSessao("processos_busca_v2", "");
  const [pagina, setPagina] = useEstadoSessao("processos_pagina_v2", 0);
  const [abertas, setAbertas] = useEstadoSessao("processos_abertas_v2", {});   // linhas expandidas (várias ao mesmo tempo)
  const [parados, setParados] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem(PARADOS_KEY) || "", 10);
      return PARADOS_OPTIONS.some((o) => o.days === v) ? v : PARADOS_DEFAULT;
    } catch (e) { return PARADOS_DEFAULT; }
  });
  const setParadosPersist = (d) => {
    setParados(d);
    try { localStorage.setItem(PARADOS_KEY, String(d)); } catch (e) { /* vale só nesta visita */ }
  };
  const [folderOpen, setFolderOpen] = useState({});   // override manual das pastas do "Por cliente"
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [vincular, setVincular] = useState(null);     // processo que vai receber cliente
  const [revDialog, setRevDialog] = useState(null);   // { p, qr } — confirmação do "Já revisei todos" / "Ata sem prazos"
  const [revBusy, setRevBusy] = useState(false);

  // Lote 157 — quem está num escritório escolhe "Meus processos" ou "Todo o escritório" (+ responsável, nos Filtros).
  const [escopo, setEscopo] = useEscopo();
  const escopoEscritorio = noEscritorio && escopo.escopo === "escritorio";
  const info = useEscritorioInfo(noEscritorio);

  // Visualização (Lista | Por cliente) e ordenação: preferências por pessoa e por escritório.
  const [view, setViewRaw] = useState(() => prefsRef.current.view);
  const [ordem, setOrdemRaw] = useState(() => prefsRef.current.ordem);
  const gravarPrefs = (extra) => {
    try {
      const atual = { view, ordem, filtros: { cliente: f.cliente, area: f.area, tribunal: f.tribunal, local: f.local, sit: f.sit }, ...extra };
      localStorage.setItem(`${PREFS_KEY}:${chavePrefs}`, JSON.stringify(atual));
    } catch (e) { /* vale só nesta visita */ }
  };
  const setView = (v) => { setViewRaw(v); gravarPrefs({ view: v }); };
  const setOrdem = (o) => { setOrdemRaw(o); setPagina(0); gravarPrefs({ ordem: o }); };
  // filtros: gravam quando a PESSOA muda (um filtro vindo de link, como ?cliente=, não fica como preferência)
  const f0 = useRef(f);
  useEffect(() => {
    if (f === f0.current) return;
    gravarPrefs({ filtros: { cliente: f.cliente, area: f.area, tribunal: f.tribunal, local: f.local, sit: f.sit } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f]);

  const setTab = (t) => { setTabRaw(t); setPagina(0); };
  const setSearch = (v) => { setSearchRaw(v); setPagina(0); };

  const load = async () => {
    try {
      const [p, c, s] = await Promise.all([
        noEscritorio
          ? http.get("/escritorio/processos", { params: { escopo: escopo.escopo, responsavel: escopo.responsavel || undefined } })
          : http.get("/processos"),
        http.get("/clients"),
        http.get("/processos-datajud/status").catch(() => ({ data: null })),
      ]);
      if (noEscritorio) registrarDonos(p.data, meuId, "processo_id");
      setItems(p.data);
      setClients(c.data);
      setDjStatus(s.data);
      setErroCarga(null);
    } catch (e) {
      // Falha ao carregar ≠ lista vazia. Se já havia lista na tela, ela continua (atualização em segundo plano falhou).
      const sem = e?.response?.status === 403;
      if (sem && noEscritorio && escopo.escopo === "escritorio") {
        // Lote 166: acesso ao escritório inteiro foi retirado → volta sozinho para "Meus processos" (recarrega pelo escopo).
        setEscopo({ escopo: "minha", responsavel: "" });
        toast.info("Você não tem acesso a todo o escritório", { description: "Mostrando só os seus processos." });
        return;
      }
      setItems((cur) => {
        if (cur === null) setErroCarga(sem ? "permissao" : "geral");
        return cur;
      });
    }
  };
  useEffect(() => { load(); }, [noEscritorio, escopo.escopo, escopo.responsavel]); // eslint-disable-line react-hooks/exhaustive-deps
  useAutoRefresh(load);   // ao voltar para a tela e a cada 15min

  // Lote 159 — "Selecionar": marcar vários processos como particulares (ou voltar a compartilhar) de uma vez.
  const [selMode, setSelMode] = useState(false);
  const [sel, setSel] = useState([]);                       // processo_id marcados
  const [selBusy, setSelBusy] = useState(false);
  const minhas = (p) => !p.dono_id || p.dono_id === meuId;  // só dá para mexer nos processos da própria conta
  const sairSel = () => { setSelMode(false); setSel([]); };
  const toggleSel = (p) => {
    if (!minhas(p)) return;
    setSel((s) => (s.includes(p.processo_id) ? s.filter((x) => x !== p.processo_id) : [...s, p.processo_id]));
  };
  const aplicarParticular = async (ids, value) => {
    if (!ids.length) return;
    setSelBusy(true);
    const r = await marcarParticular("processo", ids, value);
    setSelBusy(false);
    if (r) { sairSel(); await load(); }
  };
  const alternarParticular = async (p) => aplicarParticular([p.processo_id], !p.particular);

  const nProc = (n) => `${n} ${n === 1 ? "processo" : "processos"}`;
  const clientName = (id) => {
    const c = clients.find((x) => x.client_id === id);
    return c?.name ? toTitleCasePt(c.name) : "";
  };

  // Opções dos filtros (só o que existe nos processos carregados)
  const procCountByClient = useMemo(() => {
    const m = {};
    (items || []).forEach((p) => { if (p.client_id && !p.arquivado) m[p.client_id] = (m[p.client_id] || 0) + 1; });
    return m;
  }, [items]);
  const areaOptions = useMemo(() => {
    const present = new Set((items || []).map((p) => p.case_area).filter(Boolean));
    const known = CASE_AREAS.filter((a) => present.has(a.value));
    const extra = [...present].filter((v) => !CASE_AREAS.some((a) => a.value === v)).map((v) => ({ value: v, label: toTitleCasePt(v) }));
    return [...known, ...extra];
  }, [items]);
  const tribunalOptions = useMemo(
    () => [...new Set((items || []).map((p) => p.tribunal_alias).filter(Boolean))].sort(),
    [items],
  );
  const localOptions = useMemo(
    () => [...new Set((items || []).map(localOf).filter(Boolean))].sort((a, b) => a.localeCompare(b, "pt-BR")),
    [items],
  );

  // Lote 166 — revalida o que ficou lembrado: some o filtro de cliente/ação/tribunal/comarca que não existe mais, "sem responsável"
  // sem escritório e o responsável que saiu do escritório. (Chega junto com a lista, então vale também para o escopo escolhido.)
  useEffect(() => {
    if (items === null) return;
    setF((cur) => {
      let n = cur;
      const poe = (k, v) => { n = { ...n, [k]: v }; };
      if (cur.cliente !== "all" && !clients.some((c) => c.client_id === cur.cliente)) poe("cliente", "all");
      if (cur.area !== "all" && !areaOptions.some((a) => a.value === cur.area)) poe("area", "all");
      if (cur.tribunal !== "all" && !tribunalOptions.includes(cur.tribunal)) poe("tribunal", "all");
      if (cur.local !== "all" && !localOptions.includes(cur.local)) poe("local", "all");
      if (!noEscritorio && cur.sit.includes("sem_resp")) poe("sit", cur.sit.filter((k) => k !== "sem_resp"));
      return n === cur ? cur : n;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, clients]);
  useEffect(() => {
    if (!noEscritorio || !info || !escopo.responsavel) return;
    const ids = [...(info.membros || []), ...(info.ex_membros || [])].map((m) => m.user_id);
    if (!ids.includes(escopo.responsavel)) setEscopo({ escopo: escopo.escopo, responsavel: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info]);

  // Voltar de um processo devolve a lista no mesmo ponto da rolagem (as linhas abertas e os filtros já voltam da sessão).
  const salvarRolagem = () => {
    try { sessionStorage.setItem(SCROLL_KEY, JSON.stringify({ w: window.scrollY || 0, m: document.querySelector("main")?.scrollTop || 0 })); } catch (e) { /* sem armazenamento */ }
  };
  const ir = (to) => { salvarRolagem(); navigate(to); };      // toda saída para um processo/ação guarda o ponto da rolagem
  const rolagemOk = useRef(false);
  useEffect(() => {
    if (items === null || rolagemOk.current) return;
    rolagemOk.current = true;
    if (navType !== "POP") { try { sessionStorage.removeItem(SCROLL_KEY); } catch (e) { /* sem armazenamento */ } return; }
    try {
      const v = JSON.parse(sessionStorage.getItem(SCROLL_KEY) || "null");
      if (v) requestAnimationFrame(() => { window.scrollTo(0, v.w || 0); const m = document.querySelector("main"); if (m) m.scrollTop = v.m || 0; });
    } catch (e) { /* sem armazenamento */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const responsavelAtivo = escopoEscritorio ? (escopo.responsavel || "") : "";
  const nFiltros = contarFiltros(f, responsavelAtivo);
  const limparTudo = () => { setF(FILTROS_PADRAO); setPagina(0); if (responsavelAtivo) setEscopo({ escopo: "escritorio", responsavel: "" }); };
  const aplicarFiltros = (novo, resp, dias) => {
    setF(novo);
    setParadosPersist(dias);
    setPagina(0);
    if (noEscritorio && escopo.escopo === "escritorio" && (resp || "") !== (escopo.responsavel || "")) setEscopo({ escopo: "escritorio", responsavel: resp || "" });
  };

  // Busca + filtros: as contagens das abas respeitam todos eles (e contam processos únicos)
  const base = useMemo(
    () => filtrarProcessos(items, { f, busca: search, nomeCliente: clientName, parados }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, f, search, parados, clients],
  );
  const counts = useMemo(() => ({
    todos: base.length,
    atencao: base.filter(ABAS.atencao).length,
    prioritarios: base.filter(ABAS.prioritarios).length,
  }), [base]);
  const list = useMemo(
    () => ordenarProcessos(base.filter(ABAS[tab] || ABAS.todos), ordem, clientName),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [base, tab, ordem, clients],
  );

  const totalPaginas = Math.max(1, Math.ceil(list.length / POR_PAGINA));
  const pg = Math.min(pagina, totalPaginas - 1);
  const pageItems = useMemo(() => list.slice(pg * POR_PAGINA, (pg + 1) * POR_PAGINA), [list, pg]);

  // "Por cliente": uma pasta por cliente + "Sem cliente vinculado" no fim
  const folders = useMemo(() => {
    const m = new Map();
    list.forEach((p) => {
      const k = p.client_id || "__none__";
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(p);
    });
    const arr = [...m.entries()].map(([k, l]) => ({
      key: k,
      label: k === "__none__" ? "Sem cliente vinculado" : (clientName(k) || "Cliente"),
      list: l,
      atencao: l.filter(ABAS.atencao).length,
      star: l.some((p) => p.prioritario),
    }));
    arr.sort((a, b) => (a.key === "__none__") - (b.key === "__none__") || a.label.localeCompare(b.label, "pt-BR"));
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, clients]);

  const isFiltering = nFiltros > 0 || search.trim().length > 0 || tab !== "todos";
  const nAtivos = (items || []).filter((p) => !p.arquivado).length;
  const nPausados = (items || []).length - nAtivos;

  // ---------- ações ----------
  const patchItem = (id, patch) => setItems((cur) => (cur || []).map((p) => (p.processo_id === id ? { ...p, ...patch } : p)));

  const toggleStar = async (p) => {
    const value = !p.prioritario;
    patchItem(p.processo_id, { prioritario: value, novas_prioritario: 0 });
    try {
      await http.post(`/processos/${p.processo_id}/prioritario`, { value });
      toast.success(value ? "Processo marcado como prioritário" : "Processo deixou de ser prioritário",
        value ? { description: "Movimentações novas aparecem no Painel." } : undefined);
    } catch (e) {
      patchItem(p.processo_id, { prioritario: !value });
      toast.error("Não consegui salvar. Tente de novo.");
    }
  };

  const setArquivado = async (p, value, silent = false, motivo = "arquivado") => {
    const antes = { arquivado: !!p.arquivado, situacao_arquivo: p.situacao_arquivo || null };
    const filtrosAntes = f;
    patchItem(p.processo_id, { arquivado: value, situacao_arquivo: value ? motivo : null });
    try {
      await http.post(`/processos/${p.processo_id}/arquivar`, { value, motivo });
      if (silent) return;
      if (value) {
        toast.success(motivo === "extinto" ? "Processo marcado como extinto" : "Processo arquivado", {
          description: "A consulta ao DataJud/DJEN foi pausada. Ele fica em “Arquivados e extintos” (Filtros).",
          duration: 6000,
          action: { label: "Desfazer", onClick: () => { setArquivado(p, false, true); setF(filtrosAntes); } },
        });
        setF((cur) => ({ ...cur, arq: motivo === "extinto" ? "extintos" : "arquivados" }));   // leva para onde ele foi
        setPagina(0);
      } else {
        toast.success("Processo reativado");
      }
      load();
    } catch (e) {
      patchItem(p.processo_id, antes);
      toast.error("Não consegui salvar. Tente de novo.");
    }
  };

  const linkCliente = async (p, clientId) => {
    try {
      await http.post(`/processos/${p.processo_id}/cliente`, { client_id: clientId });
      patchItem(p.processo_id, { client_id: clientId });
      toast.success(clientId ? "Cliente vinculado" : "Cliente desvinculado");
      setVincular(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não consegui vincular o cliente");
    }
  };

  const revisarItens = async (p, pubIds, silentDialog = false) => {
    setRevBusy(true);
    try {
      const r = await http.post(`/processos/${p.processo_id}/revisar`, { pub_ids: pubIds });
      const ok = r.data?.validados || [];
      const falhou = (r.data?.falharam || []).length;
      if (ok.length) {
        toast.success(ok.length === 1 ? "Item marcado como revisado" : `${ok.length} itens marcados como revisados`, {
          description: falhou ? `${falhou} continua${falhou > 1 ? "m" : ""} pendente${falhou > 1 ? "s" : ""} (falta data ou outro dado).` : undefined,
          duration: 6000,
          action: {
            label: "Desfazer",
            onClick: async () => {
              try { await http.post(`/processos/${p.processo_id}/revisar/desfazer`, { pub_ids: ok }); load(); }
              catch (e) { toast.error("Não consegui desfazer"); }
            },
          },
        });
      } else {
        toast.warning("Nada foi marcado", { description: "Os itens estão sem data completa. Abra o item para completar." });
      }
      if (!silentDialog) setRevDialog(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não consegui marcar como revisado");
    } finally { setRevBusy(false); }
  };

  const ataSemPrazos = async (p, pendenciaId) => {
    setRevBusy(true);
    try {
      await http.post(`/prazos/${pendenciaId}/dispensar`, null, comoConfig(p?.dono_id, meuId));
      toast.success("Ata dispensada", { description: "Nenhum prazo foi criado a partir dela." });
      setRevDialog(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Não consegui dispensar a ata");
    } finally { setRevBusy(false); }
  };

  const runQuick = (p, qr) => {
    if (qr.mode === "um") revisarItens(p, [qr.item.pub_id], true);
    else setRevDialog({ p, qr });
  };

  const del = async (id) => {
    if (!confirm("Excluir este processo? O histórico de andamentos dele também é apagado.")) return;
    await http.delete(`/processos/${id}`);
    toast.info("Processo excluído");
    load();
  };

  // Lote 76: consulta só os processos cadastrados, em segundo plano (sair da tela não interrompe).
  // A busca completa (DJEN por OAB + DataJud) continua automática às 6h e 16h.
  const sync = useSyncJob("datajud", (job) => {
    if (job.status === "error") {
      toast.error(job.error || "Erro ao consultar o DataJud");
    } else {
      const d = job.summary || {};
      toast.success("Processos atualizados no DataJud", {
        description: `${d.processes_checked ?? 0} processo(s) consultado(s) · ${d.movements_imported ?? 0} movimentação(ões) nova(s) no histórico · ` +
          `${d.hearings_queued ?? 0} audiência(s) agendada(s)` + (d.errors ? ` · ${d.errors} com erro` : "") +
          (d.unsupported_alias ? ` · ${d.unsupported_alias} sem tribunal reconhecido` : ""),
      });
    }
    load();
  });
  const syncing = sync.running;

  // Atualiza UM processo (menu "…" / expansão): consulta rápida, responde na hora.
  const [oneBusy, setOneBusy] = useState(null);
  const syncOne = async (id) => {
    setOneBusy(id);
    try {
      const r = await http.post(`/processos/${id}/sync-datajud`);
      const d = r.data || {};
      if (d.errors || d.unsupported_alias) {
        toast.warning("Não consegui consultar este processo agora", { description: "Confira o número do processo ou tente de novo em instantes." });
      } else {
        toast.success("Processo atualizado", {
          description: d.movements_imported > 0 ? `${d.movements_imported} movimentação(ões) nova(s) no histórico.` : "Nenhuma movimentação nova.",
        });
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao atualizar o processo");
    } finally { setOneBusy(null); }
  };

  const abrir = (p) => ir(comDe(`/processos/${p.processo_id}`, p.dono_id, meuId));
  const runCta = (p, cta) => {
    if (cta.kind === "link") ir(comDe(cta.to, p.dono_id, meuId));
    else if (cta.kind === "atualizar") syncOne(p.processo_id);
    else if (cta.kind === "vincular") setVincular(p);
    else if (cta.kind === "reativar") setArquivado(p, false);
  };

  // Relatório .xlsx — exporta exatamente o que está na lista agora (filtrado ou não).
  const downloadBlob = (file, filename) => {
    const url = URL.createObjectURL(file);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("Relatório exportado", { description: filename });
  };
  const exportReport = async () => {
    setExporting(true);
    const today = new Date().toISOString().slice(0, 10);
    const filename = `iapauta-processos-${today}.xlsx`;
    let file;
    try {
      const body = isFiltering ? { processo_ids: list.map((p) => p.processo_id) } : {};
      const r = await http.post("/processos/export", body, { responseType: "blob" });
      file = new File([r.data], filename, { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    } catch (e) {
      toast.error("Erro ao exportar o relatório");
      setExporting(false);
      return;
    }
    try {
      if (navigator.canShare && navigator.canShare({ files: [file] })) await navigator.share({ files: [file], title: "Processos IApauta" });
      else downloadBlob(file, filename);
    } catch (e) {
      if (e?.name !== "AbortError") downloadBlob(file, filename);
    } finally {
      setExporting(false);
    }
  };

  const openFilters = () => {
    setIsMobile(typeof window !== "undefined" && window.innerWidth < 768);
    setFiltersOpen(true);
  };
  const toggleAberta = (id) => setAbertas((cur) => ({ ...cur, [id]: !cur[id] }));

  const syncWhen = syncMoment(djStatus?.last_at);
  const acoes = {
    abrir, toggleSel, runCta, runQuick, setVincular, syncOne, setArquivado, del, alternarParticular, toggleStar,
    compartilhar: (p) => ir(`/compartilhamentos?novo=processo&ref=${p.processo_id}`),
    reload: load,
  };
  const linha = (p) => (
    <ProcessoLinha
      key={p.processo_id}
      p={p}
      aberta={!!abertas[p.processo_id]}
      onToggle={() => toggleAberta(p.processo_id)}
      nomeCliente={clientName}
      parados={parados}
      noEscritorio={noEscritorio}
      meuId={meuId}
      selMode={selMode}
      selecionado={sel.includes(p.processo_id)}
      minhas={minhas}
      clients={clients}
      oneBusy={oneBusy}
      revBusy={revBusy}
      a={acoes}
    />
  );

  // ---------- chips removíveis dos filtros ----------
  const chipFiltro = (key, text, onClear) => (
    <button
      key={key}
      type="button"
      data-testid={`filter-chip-${key}`}
      aria-label={`Remover filtro: ${text}`}
      onClick={onClear}
      className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-stone-300 bg-white text-xs font-medium text-stone-700 hover:border-stone-400"
    >
      {text} <X className="w-3.5 h-3.5" strokeWidth={2} />
    </button>
  );
  const nomeResp = (id) => {
    const m = [...(info?.membros || []), ...(info?.ex_membros || [])].find((x) => x.user_id === id);
    return m?.nome || "—";
  };
  const setCampo = (k, v) => { setF((cur) => ({ ...cur, [k]: v })); setPagina(0); };
  const semSit = (k) => { setF((cur) => ({ ...cur, sit: cur.sit.filter((x) => x !== k) })); setPagina(0); };

  const TABS = [
    { key: "todos", label: "Todos" },
    { key: "atencao", label: "Atenção", dot: "bg-blue-500" },
    { key: "prioritarios", label: "Prioritários", star: true },
  ];

  // ---------- seleção em lote ----------
  const barraSelecao = (() => {
    if (!selMode) return null;
    const escolhidos = (items || []).filter((p) => sel.includes(p.processo_id));
    const aEsconder = escolhidos.filter((p) => !p.particular).map((p) => p.processo_id);
    const aMostrar = escolhidos.filter((p) => p.particular).map((p) => p.processo_id);
    const daPagina = (view === "cliente" ? list : pageItems).filter(minhas).map((p) => p.processo_id);
    const deTodos = list.filter(minhas).map((p) => p.processo_id);
    const igual = (a, b) => a.length > 0 && a.length === b.length && a.every((x) => b.includes(x));
    return (
      <div className="fixed inset-x-0 bottom-[5.25rem] md:bottom-4 z-40 px-3 pointer-events-none">
        <div data-testid="selecionar-barra" className="pointer-events-auto mx-auto max-w-3xl rounded-xl border border-stone-300 bg-white shadow-lg px-3 py-2.5 flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-stone-800 mr-auto" data-testid="selecionar-contagem">
            {sel.length === 0 ? "Nenhum selecionado" : `${sel.length} ${sel.length === 1 ? "selecionado" : "selecionados"}`}
          </span>
          {deTodos.length > daPagina.length && (
            <Button type="button" size="sm" variant="ghost" data-testid="selecionar-pagina" className="text-stone-600"
              onClick={() => setSel(igual(sel, daPagina) ? [] : daPagina)}>
              {igual(sel, daPagina) ? "Limpar" : `${view === "cliente" ? "Os desta lista" : "Esta página"} (${daPagina.length})`}
            </Button>
          )}
          <Button type="button" size="sm" variant="ghost" data-testid="selecionar-todos" className="text-stone-600"
            onClick={() => setSel(igual(sel, deTodos) ? [] : deTodos)}>
            {igual(sel, deTodos) ? "Limpar" : deTodos.length > daPagina.length ? `Todos os resultados (${deTodos.length})` : `Todos os meus (${deTodos.length})`}
          </Button>
          <Button type="button" size="sm" variant="outline" data-testid="selecionar-compartilhar" title="Voltar a compartilhar com o escritório" disabled={selBusy || aMostrar.length === 0}
            onClick={() => aplicarParticular(aMostrar, false)} className="gap-1.5 border-stone-300 flex-1 min-w-[10rem]">
            <LockOpen className="w-4 h-4" strokeWidth={1.75} /> Compartilhar{aMostrar.length ? ` (${aMostrar.length})` : ""}
          </Button>
          <Button type="button" size="sm" data-testid="selecionar-particular" title="Marcar como particular (o escritório não vê)" disabled={selBusy || aEsconder.length === 0}
            onClick={() => aplicarParticular(aEsconder, true)} className="gap-1.5 bg-stone-700 text-white hover:bg-stone-800 flex-1 min-w-[10rem]">
            <Lock className="w-4 h-4" strokeWidth={1.75} /> Particular{aEsconder.length ? ` (${aEsconder.length})` : ""}
          </Button>
        </div>
      </div>
    );
  })();

  const btnBarra = "inline-flex items-center justify-center gap-2 h-11 px-3.5 rounded-md border border-stone-300 bg-white text-sm font-medium text-stone-700 hover:border-stone-400 hover:bg-stone-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-400";

  return (
    <div className={`p-4 md:p-8 lg:p-10 max-w-6xl mx-auto md:pb-8 ${selMode ? "pb-56" : "pb-24"}`}>
      <header className="flex items-center justify-between gap-3 mb-5">
        <div className="min-w-0">
          <h1 className="font-display text-2xl md:text-3xl font-bold text-stone-900 flex items-baseline gap-2.5">
            Processos
            {items !== null && (
              <span className="text-base font-medium text-stone-500 tabular-nums" data-testid="processos-total" title={nPausados > 0 ? `${nPausados} arquivado(s)/extinto(s) fora da contagem` : undefined}>
                {nAtivos}
              </span>
            )}
          </h1>
          <p className="text-xs text-stone-500 mt-0.5 flex items-center gap-1.5" data-testid="processos-sync-bar">
            {syncing ? <RefreshCw className="w-3 h-3 animate-spin" strokeWidth={2} /> : null}
            <span data-testid="processos-sync-status">
              {syncing ? "Atualizando…" : syncWhen ? `Atualizado ${syncWhen}` : "Monitoramento ainda não atualizado"}
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button data-testid="new-processo-button" onClick={() => setOpen(true)} className="bg-stone-700 hover:bg-stone-800 text-white shadow-none gap-1.5 h-11 px-3.5">
            <Plus className="w-4 h-4" strokeWidth={2} /> <span className="sm:hidden">Novo</span><span className="hidden sm:inline">Novo processo</span>
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" aria-label="Mais opções" data-testid="processos-menu" className={`${btnBarra} w-11 px-0`}>
                <MoreHorizontal className="w-5 h-5" strokeWidth={1.75} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[15rem]">
              {noEscritorio && (
                <DropdownMenuItem data-testid="selecionar-entrar" onSelect={() => setSelMode(true)} className="gap-2 min-h-[2.5rem]">
                  <ListChecks className="w-4 h-4" strokeWidth={1.75} /> Selecionar processos
                </DropdownMenuItem>
              )}
              <DropdownMenuItem data-testid="sync-datajud-button" disabled={syncing || !items || items.length === 0} onSelect={() => sync.start()} className="gap-2 min-h-[2.5rem]">
                <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} strokeWidth={1.75} /> Atualizar monitoramento
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="importar-processos-button" onSelect={() => setImportarOpen(true)} className="gap-2 min-h-[2.5rem]">
                <Upload className="w-4 h-4" strokeWidth={1.75} /> Importar planilha
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="export-processos-button" disabled={exporting || list.length === 0} onSelect={exportReport} className="gap-2 min-h-[2.5rem]">
                <Download className="w-4 h-4" strokeWidth={1.75} /> {exporting ? "Exportando…" : "Exportar planilha"}
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="processos-how-button" onSelect={() => setHowOpen(true)} className="gap-2 min-h-[2.5rem]">
                <Info className="w-4 h-4" strokeWidth={1.75} /> Como funciona?
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <ImportarProcessos open={importarOpen} onOpenChange={setImportarOpen} onImported={() => { invalidarProcessosMapa(); load(); }} />
          <ProcessoFormDialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setNovoInicial(null); }} initial={novoInicial} onSaved={(d) => { invalidarProcessosMapa(); load(d); }} />
        </div>
      </header>

      {selMode && (
        <div className="mb-4 flex items-center gap-2">
          <div data-testid="selecionar-dica" className="flex-1 min-w-0 rounded-lg border border-stone-200 bg-white px-3 py-2 text-xs text-stone-600 flex items-center gap-2">
            <Lock className="w-4 h-4 text-stone-500 shrink-0" strokeWidth={1.75} />
            <span><b>Particular</b>: o escritório não vê o processo nem os eventos dele — só você. Toque nos seus processos para escolher.</span>
          </div>
          <Button type="button" variant="outline" size="sm" data-testid="selecionar-sair" onClick={sairSel} className="gap-2 border-stone-300 shrink-0 h-10">
            <X className="w-4 h-4" strokeWidth={1.75} /> Cancelar seleção
          </Button>
        </div>
      )}

      <Dialog open={howOpen} onOpenChange={setHowOpen}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="processos-how-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Como funciona a aba Processos</DialogTitle>
            <DialogDescription>O que o app faz com cada número CNJ que você cadastra.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 text-sm text-stone-700">
            <p>
              Cadastre o número CNJ e o app passa a acompanhar o processo no <strong>DataJud/CNJ</strong>,
              detectando audiências mesmo quando o DJEN estiver indisponível. Toque no nome do processo para abrir os dados completos
              e o histórico de publicações e andamentos; toque na seta (ou na linha) para ver os detalhes sem sair da lista.
            </p>
            <div>
              <h3 className="font-semibold text-stone-900 mb-1">Quando o app consulta</h3>
              <p>
                O DJEN e o DataJud são consultados sozinhos às 6h e às 16h (horário de Brasília). “Atualizar monitoramento”, no menu ⋯,
                força uma nova consulta ao DataJud agora. O texto “Atualizado …” mostra a última consulta que deu certo.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-stone-900 mb-1">Abas</h3>
              <p>
                <strong>Todos</strong> mostra tudo o que está nos filtros. <strong>Atenção</strong> reúne os processos com uma pendência concreta:
                publicação, audiência ou prazo esperando a sua conferência, ata para baixar, prazo já vencido sem baixa ou revisão periódica vencida.
                Uma audiência futura já confirmada não é pendência (ela aparece como próxima ação, mas não em Atenção).
                <strong>Prioritários</strong> são os que você marcou com a estrela.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-stone-900 mb-1">Próxima ação e ordem da lista</h3>
              <p>
                A coluna “Próxima ação” mostra o que fazer e por quê, na ordem em que vence: dar baixa em prazo vencido, conferir o que está pendente,
                audiência ou prazo mais próximo e, por último, a revisão periódica. As demais aparecem como “+N ações” e, abrindo a linha, em “Demais
                providências”. Sem nada disso, a linha diz “Nenhuma providência cadastrada”. No botão de ordem você escolhe: próxima ação (padrão, com o
                que já venceu primeiro), última movimentação, cliente (A–Z) ou cadastro mais recente; a estrela não muda a ordem (para ver só os
                prioritários use a aba). A visualização, a ordem e os filtros ficam lembrados neste aparelho.
              </p>
              <p className="mt-2">
                Abrindo a linha você encontra “Já revisado” (ou “Já revisei todos”), que confirma os itens sem abrir a tela e tem “Desfazer”,
                além de “Ata sem prazos”, “Atualizar agora” e “Vincular cliente”.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-stone-900 mb-1">★ Prioritários</h3>
              <p>
                A estrela marca os processos que você quer vigiar de perto. Quando chega movimentação nova de
                um prioritário, ela aparece num card no Painel, com “Abrir processo” e “Visto”.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-stone-900 mb-1">Filtros, “sem movimentação capturada” e arquivados</h3>
              <p>
                No botão Filtros você escolhe cliente, responsável, tipo de ação, tribunal, comarca e situação, e toca em “Aplicar filtros”.
                “Sem movimentação capturada” mostra processos sem movimento <em>capturado</em> há mais de 6 meses (você muda o prazo): o app só sabe
                o que o DJEN e o DataJud devolvem, então o texto não diz que o processo está parado no tribunal. “Arquivado” pausa a consulta no app;
                “extinto” é a marca de processo que acabou. Os dois saem da lista normal e voltam em Filtros → Cadastro no app; dá para reativar.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-stone-900 mb-1">Visualização “Por cliente” e planilha</h3>
              <p>
                No menu Visualização você alterna entre Lista e Por cliente (pastas); a escolha fica lembrada. “Exportar planilha”, no menu ⋯,
                gera o .xlsx com exatamente o que está na lista naquele momento.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Barra de ferramentas */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[14rem]">
          <Search className="w-4 h-4 text-stone-400 absolute left-3.5 top-1/2 -translate-y-1/2 z-10 pointer-events-none" strokeWidth={2} />
          <SugestaoInput
            type="text"
            testid="processo-filter-text"
            fonte={["clientes", "partes", "varas", "comarcas", "apelidos"]}
            value={search}
            onValueChange={setSearch}
            placeholder="Buscar processo, parte ou cliente"
            className="w-full pl-10 pr-9 h-11 text-sm rounded-md border border-stone-200 bg-white shadow-none focus:outline-none focus:ring-2 focus:ring-stone-400"
          />
          {search && (
            <button type="button" data-testid="processo-filter-text-clear" aria-label="Limpar busca" onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 z-10 text-stone-400 hover:text-stone-700">
              <X className="w-4 h-4" strokeWidth={2} />
            </button>
          )}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {noEscritorio && (
            <div data-testid="escopo-pauta" className="relative">
              <select
                data-testid="escopo-select"
                aria-label="Quais processos mostrar"
                value={escopo.escopo}
                onChange={(e) => { setEscopo({ escopo: e.target.value, responsavel: e.target.value === "minha" ? "" : escopo.responsavel }); setPagina(0); }}
                className="appearance-none h-11 pl-3.5 pr-9 rounded-md border border-stone-300 bg-white text-sm font-medium text-stone-700 hover:border-stone-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-400"
              >
                <option value="minha">Meus processos</option>
                <option value="escritorio">Todo o escritório</option>
              </select>
              <ChevronDown className="w-4 h-4 text-stone-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" strokeWidth={2} />
            </div>
          )}
          <button type="button" data-testid="processos-filter-menu" aria-label="Filtros" onClick={openFilters} className={`${btnBarra} relative`}>
            <SlidersHorizontal className="w-4 h-4" strokeWidth={1.75} /> Filtros
            {nFiltros > 0 && (
              <span className="min-w-[1.25rem] h-5 px-1 rounded-full bg-gold-500 text-black text-[11px] font-bold flex items-center justify-center" data-testid="processos-filter-count">
                {nFiltros}
              </span>
            )}
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" data-testid="processos-view" aria-label={`Visualização: ${view === "cliente" ? "Por cliente" : "Lista"}`} className={btnBarra}>
                {view === "cliente" ? <Users className="w-4 h-4" strokeWidth={1.75} /> : <List className="w-4 h-4" strokeWidth={1.75} />}
                <span className="hidden sm:inline">{view === "cliente" ? "Por cliente" : "Lista"}</span>
                <ChevronDown className="w-3.5 h-3.5 text-stone-500" strokeWidth={2} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem data-testid="processos-view-lista" onSelect={() => setView("lista")} className="gap-2 min-h-[2.5rem]">
                <List className="w-4 h-4" strokeWidth={1.75} /> Lista {view === "lista" && <Check className="w-4 h-4 ml-auto" strokeWidth={2} />}
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="processos-view-cliente" onSelect={() => setView("cliente")} className="gap-2 min-h-[2.5rem]">
                <Users className="w-4 h-4" strokeWidth={1.75} /> Por cliente {view === "cliente" && <Check className="w-4 h-4 ml-auto" strokeWidth={2} />}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" data-testid="processos-ordem" aria-label={`Ordenar por: ${ORDENS.find((o) => o.key === ordem)?.label}`} className={btnBarra}>
                <ArrowUpDown className="w-4 h-4" strokeWidth={1.75} />
                <span className="hidden lg:inline">{ORDENS.find((o) => o.key === ordem)?.label}</span>
                <ChevronDown className="w-3.5 h-3.5 text-stone-500" strokeWidth={2} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {ORDENS.map((o) => (
                <DropdownMenuItem key={o.key} data-testid={`processos-ordem-${o.key}`} onSelect={() => setOrdem(o.key)} className="gap-2 min-h-[2.5rem]">
                  {o.label} {ordem === o.key && <Check className="w-4 h-4 ml-auto" strokeWidth={2} />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Abas */}
      <div className="flex gap-7 border-b border-stone-200 mb-4 overflow-x-auto" role="tablist" aria-label="Situação" data-testid="processos-chips">
        {TABS.map((t) => {
          const on = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={on}
              data-testid={`processos-chip-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`shrink-0 inline-flex items-center gap-2 h-11 px-0.5 -mb-px border-b-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-400 rounded-sm ${on ? "border-stone-800 text-stone-900" : "border-transparent text-stone-500 hover:text-stone-800"}`}
            >
              {t.star && <Star className="w-3.5 h-3.5 fill-gold-500 text-gold-500" strokeWidth={1.75} />}
              {t.dot && <span className={`w-2 h-2 rounded-full ${t.dot}`} />}
              {t.label}
              <span className={`text-xs tabular-nums px-1.5 py-0.5 rounded ${on ? "bg-stone-200 text-stone-800" : "bg-stone-100 text-stone-500"}`} data-testid={`processos-contagem-${t.key}`}>{counts[t.key] ?? 0}</span>
            </button>
          );
        })}
      </div>

      {(nFiltros > 0) && (
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="processos-active-filters">
          {f.cliente !== "all" && chipFiltro("cliente", `Cliente: ${clientName(f.cliente) || "—"}`, () => setCampo("cliente", "all"))}
          {responsavelAtivo && chipFiltro("resp", `Responsável: ${nomeResp(responsavelAtivo)}`, () => { setEscopo({ escopo: "escritorio", responsavel: "" }); setPagina(0); })}
          {f.area !== "all" && chipFiltro("area", `Ação: ${areaOptions.find((a) => a.value === f.area)?.label || f.area}`, () => setCampo("area", "all"))}
          {f.tribunal !== "all" && chipFiltro("tribunal", `Tribunal: ${tribunalLabel(f.tribunal)}`, () => setCampo("tribunal", "all"))}
          {f.local !== "all" && chipFiltro("local", `Comarca: ${toTitleCasePt(f.local)}`, () => setCampo("local", "all"))}
          {f.sit.map((k) => chipFiltro(`sit-${k}`,
            k === "parados" ? `Sem movimentação capturada há mais de ${paradosLabel(parados)}` : SIT_OPCOES.find((o) => o.key === k)?.label || k,
            () => semSit(k)))}
          {f.arq !== "ativos" && chipFiltro("arq", `Cadastro: ${ARQ_ROTULO[f.arq]}`, () => setCampo("arq", "ativos"))}
          <button type="button" data-testid="processos-limpar-filtros" onClick={limparTudo} className="text-xs text-stone-500 hover:text-stone-800 underline-offset-2 hover:underline h-8 px-1">
            Limpar filtros
          </button>
        </div>
      )}

      <FiltrosPainel
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        filtros={f}
        responsavel={responsavelAtivo}
        parados={parados}
        noEscritorio={noEscritorio}
        escopoEscritorio={escopoEscritorio}
        clients={clients}
        procCountByClient={procCountByClient}
        areaOptions={areaOptions}
        tribunalOptions={tribunalOptions}
        localOptions={localOptions}
        onAplicar={aplicarFiltros}
        isMobile={isMobile}
      />

      <Dialog open={!!revDialog} onOpenChange={(o) => { if (!o && !revBusy) setRevDialog(null); }}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid="revisao-dialog">
          {revDialog?.qr.mode === "ata" ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl">A ata não trouxe prazos?</DialogTitle>
                <DialogDescription>
                  O lembrete “Baixar ata” deste processo será dispensado e nenhum prazo será criado a partir dela.
                </DialogDescription>
              </DialogHeader>
              <div className="flex gap-2">
                <Button type="button" data-testid="revisao-confirmar" disabled={revBusy} className="flex-1 bg-stone-700 hover:bg-stone-800 text-white"
                  onClick={() => ataSemPrazos(revDialog.p, revDialog.qr.item.pendencia_id)}>
                  Sim, sem prazos
                </Button>
                <Button type="button" variant="outline" className="border-stone-300" disabled={revBusy} onClick={() => setRevDialog(null)}>Cancelar</Button>
              </div>
            </>
          ) : revDialog ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl">
                  Marcar {revDialog.qr.revisaveis.length === 1 ? "1 item" : `${revDialog.qr.revisaveis.length} itens`} como {revDialog.qr.revisaveis.length === 1 ? "revisado" : "revisados"}?
                </DialogTitle>
                <DialogDescription>Os dados serão confirmados e saem do contador. Nada é apagado nem muda de data.</DialogDescription>
              </DialogHeader>
              <ul className="space-y-1.5 text-sm text-stone-800" data-testid="revisao-lista">
                {revDialog.qr.itens.map((i) => (
                  <li key={`${i.kind}-${i.pub_id}`} className={`flex items-start gap-2 ${i.kind === "pub" && i.pode_revisar ? "" : "text-stone-500"}`}>
                    <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${i.kind === "pub" && i.pode_revisar ? "bg-emerald-600" : "bg-stone-300"}`} />
                    <span className="break-words min-w-0">{itemLabel(i)}{i.kind === "pub" && !i.pode_revisar ? " (completar antes)" : ""}</span>
                  </li>
                ))}
              </ul>
              <div className="flex gap-2">
                <Button type="button" data-testid="revisao-confirmar" disabled={revBusy} className="flex-1 bg-stone-700 hover:bg-stone-800 text-white"
                  onClick={() => revisarItens(revDialog.p, revDialog.qr.revisaveis.map((i) => i.pub_id))}>
                  Sim, revisei
                </Button>
                <Button type="button" variant="outline" className="border-stone-300" disabled={revBusy} onClick={() => setRevDialog(null)}>Cancelar</Button>
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={!!vincular} onOpenChange={(o) => { if (!o) setVincular(null); }}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid="vincular-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{vincular?.client_id ? "Trocar cliente" : "Vincular cliente"}</DialogTitle>
            <DialogDescription className="tabular-nums break-words">{vincular?.numero}</DialogDescription>
          </DialogHeader>
          {clients.length === 0 ? (
            <p className="text-sm text-stone-600">Você ainda não tem clientes cadastrados. Cadastre um na aba Clientes e volte aqui.</p>
          ) : (
            <ul className="space-y-1.5">
              {clients.map((c) => (
                <li key={c.client_id}>
                  <button
                    type="button"
                    data-testid={`vincular-cliente-${c.client_id}`}
                    onClick={() => linkCliente(vincular, c.client_id)}
                    className={`w-full text-left h-11 px-3 rounded-md border text-sm font-medium hover:border-stone-400 ${vincular?.client_id === c.client_id ? "bg-stone-700 text-white border-stone-700" : "bg-white text-stone-800 border-stone-200"}`}
                  >
                    {toTitleCasePt(c.name)}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {vincular?.client_id && (
            <button type="button" data-testid="vincular-remover" onClick={() => linkCliente(vincular, null)} className="text-sm text-stone-500 hover:text-stone-800 underline-offset-2 hover:underline text-left">
              Desvincular do cliente atual
            </button>
          )}
        </DialogContent>
      </Dialog>

      {/* Resultado */}
      {items === null && erroCarga ? (
        <div className="bg-white border border-stone-200 rounded-lg p-10 text-center" data-testid="processos-erro">
          <p className="font-display text-lg text-stone-900 mb-1">
            {erroCarga === "permissao" ? "Você não tem permissão para ver estes processos" : "Não consegui carregar os processos"}
          </p>
          <p className="text-sm text-stone-500 mb-4">
            {erroCarga === "permissao"
              ? "Peça a um administrador do escritório para liberar o acesso, ou troque para “Meus processos”."
              : "Isso não quer dizer que a lista esteja vazia. Verifique a conexão e tente de novo."}
          </p>
          <Button type="button" data-testid="processos-erro-tentar" variant="outline" className="border-stone-300 gap-2"
            onClick={() => { setErroCarga(null); load(); }}>
            <RefreshCw className="w-4 h-4" strokeWidth={1.75} /> Tentar de novo
          </Button>
        </div>
      ) : items === null ? (
        <div className="p-12 text-center text-stone-500" data-testid="processos-carregando">Carregando…</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-stone-200 rounded-lg p-12 text-center" data-testid="processos-nenhum">
          <FolderKanban className="w-10 h-10 mx-auto text-stone-300 mb-3" strokeWidth={1.25} />
          <p className="font-display text-lg text-stone-900 mb-1">
            {noEscritorio && escopo.escopo === "minha" ? "Nenhum processo é seu por enquanto" : "Nenhum processo cadastrado"}
          </p>
          <p className="text-sm text-stone-500">
            {noEscritorio && escopo.escopo === "minha"
              ? "Troque para “Todo o escritório” para ver os dos colegas, ou toque em “Novo processo”."
              : "Toque em “Novo processo” para cadastrar o primeiro número CNJ."}
          </p>
        </div>
      ) : list.length === 0 ? (
        <div className="bg-white border border-stone-200 rounded-lg p-12 text-center" data-testid="processos-vazio">
          <p className="text-stone-600 text-sm mb-3">
            {tab === "prioritarios" && !isFiltering
              ? "Nenhum prioritário ainda. Toque na estrela de um processo para marcá-lo."
              : tab === "atencao" && nFiltros === 0 && !search.trim()
              ? "Nada pedindo a sua atenção agora."
              : "Nenhum processo encontrado para esse filtro."}
          </p>
          {(nFiltros > 0 || search.trim() || tab !== "todos") && (
            <Button type="button" variant="outline" size="sm" className="border-stone-300" data-testid="processos-vazio-limpar"
              onClick={() => { limparTudo(); setSearch(""); setTab("todos"); }}>
              Limpar filtros e busca
            </Button>
          )}
        </div>
      ) : view === "cliente" ? (
        <div className="space-y-3" data-testid="processos-por-cliente">
          <p className="text-xs text-stone-500" data-testid="processos-resultado">{nProc(list.length)} em {folders.length} {folders.length === 1 ? "pasta" : "pastas"}</p>
          {folders.map((fo) => {
            const isOpen = folderOpen[fo.key] ?? (fo.atencao > 0 || folders.length === 1);
            return (
              <section key={fo.key} data-testid={`processos-folder-${fo.key}`}>
                <button
                  type="button"
                  aria-expanded={isOpen}
                  data-testid={`processos-folder-toggle-${fo.key}`}
                  onClick={() => setFolderOpen((cur) => ({ ...cur, [fo.key]: !isOpen }))}
                  className="w-full flex items-center gap-2.5 h-12 px-3.5 rounded-lg border border-stone-200 bg-white text-left hover:border-stone-300"
                >
                  {isOpen ? <ChevronDown className="w-4 h-4 text-stone-500 shrink-0" strokeWidth={2} /> : <ChevronRight className="w-4 h-4 text-stone-500 shrink-0" strokeWidth={2} />}
                  <span className="font-semibold text-sm text-stone-900 truncate flex-1">{fo.label}</span>
                  {fo.atencao > 0 && <span className="text-[11px] font-medium px-2 py-0.5 rounded-full border bg-blue-50 text-blue-700 border-blue-200 shrink-0">{fo.atencao} em atenção</span>}
                  <span className="text-xs text-stone-500 tabular-nums shrink-0">{nProc(fo.list.length)}</span>
                  {fo.star && <Star className="w-4 h-4 fill-gold-500 text-gold-500 shrink-0" strokeWidth={1.75} />}
                </button>
                {isOpen && <ul className="space-y-3 mt-3">{fo.list.map(linha)}</ul>}
              </section>
            );
          })}
        </div>
      ) : (
        <div data-testid="processos-lista">
          <p className="text-xs text-stone-500 mb-3" data-testid="processos-resultado">
            {list.length > POR_PAGINA
              ? `Mostrando ${pg * POR_PAGINA + 1}–${pg * POR_PAGINA + pageItems.length} de ${nProc(list.length)}`
              : nProc(list.length)}
          </p>
          <ul className="space-y-3">{pageItems.map(linha)}</ul>
          {totalPaginas > 1 && (
            <nav className="flex items-center justify-between gap-3 mt-5" aria-label="Páginas" data-testid="processos-paginacao">
              <Button type="button" variant="outline" size="sm" data-testid="pagina-anterior" disabled={pg === 0} onClick={() => { setPagina(pg - 1); window.scrollTo({ top: 0 }); }} className="gap-1.5 border-stone-300 h-10">
                <ChevronLeft className="w-4 h-4" strokeWidth={2} /> Anterior
              </Button>
              <span className="text-sm text-stone-600 tabular-nums" data-testid="pagina-atual">Página {pg + 1} de {totalPaginas}</span>
              <Button type="button" variant="outline" size="sm" data-testid="pagina-proxima" disabled={pg >= totalPaginas - 1} onClick={() => { setPagina(pg + 1); window.scrollTo({ top: 0 }); }} className="gap-1.5 border-stone-300 h-10">
                Próxima <ChevronRight className="w-4 h-4" strokeWidth={2} />
              </Button>
            </nav>
          )}
        </div>
      )}

      {barraSelecao}
    </div>
  );
}
